import {
  GeolocateControl,
  MapLibreMap,
  type MapMouseEvent,
  NavigationControl,
  type Point,
  type PointLike,
  ScaleControl,
  setWorkerUrl,
} from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

// workaround to make sure MapLibre's service worker is emitted into the dist/ directory
import workerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url";

import { DEFAULT_VIEW, INTERACTIVE_MIN_ZOOM, WETLAND_SOURCE_LAYER } from "./config.ts";
import { type OsmType, type Ref, refsEqual } from "./osm.ts";
import { buildStyle } from "./style/index.ts";
import { WETLAND_LAYER_IDS, WETLAND_SOURCE } from "./style/wetlands.ts";

setWorkerUrl(workerUrl);

const HIT_RADIUS = 5;
const STORAGE_KEY = "owm.view";

export interface View {
  lng: number;
  lat: number;
  zoom: number;
}

export interface MapViewEvents {
  onSelect(ref: Ref | null): void;
  onChange(): void;
}

export class MapView {
  private map: MapLibreMap;
  private events: MapViewEvents;

  private selected: Ref | null;
  private hovered: Ref | null = null;
  private interactive: boolean;

  // Remembers the stack of features under the last click, so clicking the same
  // spot again walks down through overlapping wetlands.
  private lastClickKey: string | null = null;
  private lastClickIndex = 0;

  constructor(container: HTMLElement, selected: Ref | null, events: MapViewEvents) {
    const view = storedView() ?? DEFAULT_VIEW;

    const map = new MapLibreMap({
      container,
      style: buildStyle(),
      center: [view.lng, view.lat],
      zoom: view.zoom,
      // MapLibre owns the `map=` hash parameter; the app owns `selected=`.
      hash: "map",
      doubleClickZoom: false,
    });

    this.map = map;
    this.events = events;
    this.selected = selected;
    this.interactive = map.getZoom() >= INTERACTIVE_MIN_ZOOM;

    map.once("load", () => this.applyFeatureState(this.selected, "selected", true));

    map.addControl(new NavigationControl({ visualizePitch: false }), "top-right");
    map.addControl(
      new GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: true,
      }),
      "top-right",
    );
    map.addControl(new ScaleControl({ maxWidth: 120, unit: "metric" }), "bottom-left");

    map.on("click", (e) => this.didClick(e));
    map.on("mousemove", (e) => this.didMouseMove(e));
    map.on("mouseout", () => this.setHovered(null));
    map.on("zoom", () => this.didZoom());
    map.on("moveend", () => {
      storeView(this.view());
      this.events.onChange();
    });
  }

  view(): View {
    const { lng, lat } = this.map.getCenter();
    return { lng, lat, zoom: this.map.getZoom() };
  }

  isInteractive(): boolean {
    return this.interactive;
  }

  setSelected(ref: Ref | null): void {
    if (refsEqual(ref, this.selected)) return;
    this.applyFeatureState(this.selected, "selected", false);
    this.selected = ref;
    this.applyFeatureState(ref, "selected", true);
    if (refsEqual(ref, this.hovered)) this.setHovered(null);
  }

  remove(): void {
    this.map.remove();
  }

  private didZoom(): void {
    const interactive = this.map.getZoom() >= INTERACTIVE_MIN_ZOOM;
    if (interactive === this.interactive) return;
    this.interactive = interactive;
    if (!interactive) {
      this.setHovered(null);
      this.map.getCanvas().style.cursor = "";
    }
    this.events.onChange();
  }

  private didClick(e: MapMouseEvent): void {
    const refs = this.interactive ? this.refsNear(e.point) : [];
    if (refs.length === 0) {
      this.lastClickKey = null;
      this.events.onSelect(null);
      return;
    }

    const key = refs.map(refKey).join(",");
    if (key === this.lastClickKey) {
      this.lastClickIndex = (this.lastClickIndex + 1) % refs.length;
    } else {
      this.lastClickKey = key;
      this.lastClickIndex = 0;
    }
    this.events.onSelect(refs[this.lastClickIndex] ?? null);
  }

  private didMouseMove(e: MapMouseEvent): void {
    if (!this.interactive) return;
    this.setHovered(this.refsNear(e.point)[0] ?? null);
  }

  private setHovered(ref: Ref | null): void {
    const next = refsEqual(ref, this.selected) ? null : ref;
    if (refsEqual(next, this.hovered)) return;

    this.applyFeatureState(this.hovered, "hover", false);
    this.hovered = next;
    this.applyFeatureState(next, "hover", true);
    this.map.getCanvas().style.cursor = ref ? "pointer" : "";
  }

  /** Features under `point`, topmost first, deduplicated across layers. */
  private refsNear(point: Point): Ref[] {
    const box: [PointLike, PointLike] = [
      [point.x - HIT_RADIUS, point.y - HIT_RADIUS],
      [point.x + HIT_RADIUS, point.y + HIT_RADIUS],
    ];

    const seen = new Set<string>();
    const refs: Ref[] = [];
    for (const feature of this.map.queryRenderedFeatures(box, { layers: WETLAND_LAYER_IDS })) {
      const ref = decodeFeatureId(feature.id);
      if (!ref || seen.has(refKey(ref))) continue;
      seen.add(refKey(ref));
      refs.push(ref);
    }
    return refs;
  }

  private applyFeatureState(ref: Ref | null, key: string, value: boolean): void {
    if (!ref) return;
    const target = {
      source: WETLAND_SOURCE,
      sourceLayer: WETLAND_SOURCE_LAYER,
      id: encodeFeatureId(ref),
    };
    if (value) {
      this.map.setFeatureState(target, { [key]: true });
    } else {
      this.map.removeFeatureState(target, key);
    }
  }
}

const refKey = ({ type, id }: Ref) => `${type}/${id}`;

// The wetland tileset encodes OSM type and id into a single numeric feature id
// as `id * 10 + type_code`.
const TYPE_CODES: Record<OsmType, number> = { node: 1, way: 2, relation: 3 };
const TYPES: Record<number, OsmType> = { 1: "node", 2: "way", 3: "relation" };

function encodeFeatureId({ type, id }: Ref): number {
  return id * 10 + TYPE_CODES[type];
}

/** Returns null for ids that don't decode to an OSM element, e.g. merged features. */
function decodeFeatureId(featureId: number | string | undefined): Ref | null {
  const value = typeof featureId === "string" ? Number(featureId) : featureId;
  if (value === undefined || !Number.isFinite(value) || value <= 0) return null;
  const type = TYPES[value % 10];
  if (!type) return null;
  return { type, id: Math.floor(value / 10) };
}

function storedView(): View | null {
  if (location.hash.includes("map=")) return null;
  try {
    const view = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "") as Partial<View>;
    if (typeof view.lng !== "number") return null;
    if (typeof view.lat !== "number") return null;
    if (typeof view.zoom !== "number") return null;
    return { lng: view.lng, lat: view.lat, zoom: view.zoom };
  } catch {
    return null;
  }
}

function storeView(view: View): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(view));
  } catch {
    // private browser or localstorage not available
  }
}
